import { motion } from 'framer-motion';

const cloudServices = [
  {
    title: "Cloud Migration",
    icon: "🚀",
    description: "Seamless transition of your infrastructure to the cloud",
    benefits: [
      "Minimal downtime",
      "Data integrity",
      "Cost optimization",
      "Performance improvement"
    ]
  },
  {
    title: "Cloud Infrastructure",
    icon: "⚡",
    description: "Scalable and reliable cloud infrastructure solutions",
    benefits: [
      "Auto-scaling",
      "Load balancing",
      "High availability",
      "Disaster recovery"
    ]
  },
  {
    title: "Cloud Security",
    icon: "🛡️",
    description: "Advanced security measures for your cloud environment",
    benefits: [
      "Encryption",
      "Access control",
      "Threat detection",
      "Compliance management"
    ]
  }
];

const partners = [
  {
    name: "AWS",
    logo: "/path/to/aws-logo.png",
    level: "Advanced Partner"
  },
  {
    name: "Microsoft Azure",
    logo: "/path/to/azure-logo.png",
    level: "Gold Partner"
  },
  {
    name: "Google Cloud",
    logo: "/path/to/gcp-logo.png",
    level: "Premier Partner"
  }
];

const features = [
  {
    name: "24/7 Monitoring",
    description: "Continuous monitoring and alert systems",
    icon: "🔍"
  },
  {
    name: "Cost Management",
    description: "Optimize cloud spending and resource allocation",
    icon: "💰"
  },
  {
    name: "Performance Optimization",
    description: "Enhance cloud performance and efficiency",
    icon: "📈"
  }
];

export default function CloudSolutions() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section with Animated Background */}
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30"></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Cloud Solutions
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Transform your business with our comprehensive cloud solutions. 
              Scale efficiently, secure your data, and optimize performance.
            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mt-10 flex items-center justify-center gap-x-6"
            >
              <a
                href="#contact"
                className="rounded-full bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-lg hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-all duration-200"
              >
                Get Started
              </a>
              <a
                href="#learn-more"
                className="text-sm font-semibold leading-6 text-gray-900 dark:text-white flex items-center gap-2 group"
              >
                Learn more 
                <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Cloud Services Section */}
      <div className="py-24 sm:py-32 bg-gray-50 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
              Our Cloud Services
            </h2>
            <p className="mt-4 text-gray-600 dark:text-gray-300">
              Comprehensive cloud solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {cloudServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center text-gray-600 dark:text-gray-300">
                      <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <div className="absolute inset-0 border-2 border-transparent hover:border-blue-500 rounded-2xl transition-colors duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Partners Section */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Our Cloud Partners
            </h2>
            <p className="mt-4 text-gray-600 dark:text-gray-300">
              Working with leading cloud providers to deliver excellence
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-12 w-auto mb-4"
                />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {partner.name}
                </h3>
                <p className="text-blue-600 dark:text-blue-400 mt-2">
                  {partner.level}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="py-24 sm:py-32 bg-gray-50 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="rounded-2xl bg-white dark:bg-gray-900 p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative isolate overflow-hidden bg-blue-600 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to Move to the Cloud?
            </h2>
            <p className="mt-6 text-lg leading-8 text-blue-100">
              Let's discuss how our cloud solutions can transform your business
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-10 flex items-center justify-center"
            >
              <a
                href="#contact"
                className="rounded-full bg-white px-8 py-3 text-sm font-semibold text-blue-600 shadow-lg hover:bg-blue-50 transition-colors duration-200"
              >
                Contact Us Now
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
} 